// src/components/financials/CategorySettings.js
// Updated: 02/01/2026 - Removed unused Grid import

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, List, ListItem, ListItemText, ListItemSecondaryAction, 
  IconButton, TextField, Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert
} from '@mui/material';
import { Delete, Edit, Add, RestorePage } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

// --- STANDARD CHURCH CATEGORIES ---
const DEFAULT_CATEGORIES = [
    "Personnel & Salaries",
    "Facilities & Maintenance",
    "Utilities",
    "Missions & Outreach",
    "Worship & Music",
    "Children's Ministry",
    "Youth Ministry",
    "Office Supplies",
    "Technology & Software",
    "Insurance",
    "Professional Services",
    "Guest Speakers",
    "Benevolence"
];

const CategorySettings = () => {
  const { userProfile } = useAuth();
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentCat, setCurrentCat] = useState({ name: '', id: null });
  const [loading, setLoading] = useState(false);

  const churchId = userProfile?.churchId;

  // 1. Live Listen to Categories
  useEffect(() => {
    if (!churchId) return;
    const q = query(collection(db, 'churches', churchId, 'financials', 'data', 'expenseCategories'), orderBy('name'));
    const unsubscribe = onSnapshot(q, (snap) => {
      setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [churchId]);

  // 2. Load Defaults Handler
  const handleLoadDefaults = async () => {
      if (!window.confirm("This will add standard categories to your list. Continue?")) return;
      setLoading(true);
      try {
          const batch = writeBatch(db);
          DEFAULT_CATEGORIES.forEach(catName => {
              // Create a ref for a new doc
              const newRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'expenseCategories'));
              batch.set(newRef, {
                  name: catName,
                  createdAt: serverTimestamp(),
                  isDefault: true
              });
          });
          await batch.commit();
      } catch (e) {
          alert("Error loading defaults: " + e.message);
      }
      setLoading(false);
  };

  // 3. Save Handler (Add/Edit)
  const handleSave = async () => {
    if (!currentCat.name.trim()) return;
    
    try {
      if (currentCat.id) {
        // Update
        const ref = doc(db, 'churches', churchId, 'financials', 'data', 'expenseCategories', currentCat.id);
        await updateDoc(ref, { name: currentCat.name });
      } else {
        // Create
        await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'expenseCategories'), {
          name: currentCat.name,
          createdAt: serverTimestamp()
        });
      }
      setOpen(false);
      setCurrentCat({ name: '', id: null });
    } catch (e) {
      alert("Error saving category: " + e.message);
    }
  };

  // 4. Delete Handler
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This will remove the category from future selections.")) return;
    try {
      await deleteDoc(doc(db, 'churches', churchId, 'financials', 'data', 'expenseCategories', id));
    } catch (e) {
      alert("Error deleting: " + e.message);
    }
  };

  const openEdit = (cat) => {
    setCurrentCat(cat);
    setOpen(true);
  };

  const openAdd = () => {
    setCurrentCat({ name: '', id: null });
    setOpen(true);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Expense Categories</Typography>
          <Box>
            {/* Show "Load Defaults" if list is empty */}
            {categories.length === 0 && (
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    startIcon={<RestorePage />} 
                    onClick={handleLoadDefaults}
                    disabled={loading}
                    sx={{ mr: 2 }}
                >
                    Load Standard List
                </Button>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
                Add Category
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Standardize your reporting by managing categories here. These will appear in all expense forms.
        </Typography>
        
        <Divider />
        
        <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          {categories.map((cat) => (
            <React.Fragment key={cat.id}>
              <ListItem>
                <ListItemText 
                    primary={cat.name} 
                    primaryTypographyProps={{ fontWeight: 'medium' }}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => openEdit(cat)} sx={{ mr: 1 }}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleDelete(cat.id)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
          
          {categories.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                  No categories found. Click <strong>"Load Standard List"</strong> to get started quickly, or add your own manually.
              </Alert>
          )}
        </List>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{currentCat.id ? 'Edit Category' : 'New Category'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={currentCat.name}
            onChange={(e) => setCurrentCat({ ...currentCat, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategorySettings;